/**
 * PromptCreateModal — Création d'une nouvelle version de prompt agent.
 * Composant dédié (séparé de AgentConfigModal) pour réutilisabilité.
 * Bootstrap 5.3 + tokens --hrl-* uniquement. Zéro PHI dans les champs.
 */

import { useState } from 'react';

const TASK_TYPE_OPTIONS = [
  { value: 'system_prompt', label: 'system_prompt — Comportement global agent' },
  { value: 'user_prompt_template', label: 'user_prompt_template — Template message utilisateur' },
];

interface Props {
  agentId: string;
  onClose: () => void;
  onCreated: () => void;
  onCreate: (payload: {
    task_type: string;
    prompt_text: string;
    notes?: string;
    author?: string;
  }) => Promise<void>;
}

export function PromptCreateModal({ agentId, onClose, onCreated, onCreate }: Props) {
  const [taskType, setTaskType] = useState('system_prompt');
  const [promptText, setPromptText] = useState('');
  const [notes, setNotes] = useState('');
  const [author, setAuthor] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charCount = promptText.length;
  const overLimit = charCount > 16000;

  async function handleSubmit() {
    if (!promptText.trim() || overLimit) return;
    setError(null);
    setCreating(true);
    try {
      await onCreate({
        task_type: taskType,
        prompt_text: promptText.trim(),
        notes: notes.trim() || undefined,
        author: author.trim() || undefined,
      });
      onCreated();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur création version');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title mb-0">
              Nouvelle version — <span className="font-monospace small text-muted">{agentId}</span>
            </h6>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {error && <div className="alert alert-danger py-2 small">{error}</div>}

            <div className="alert alert-warning py-2 mb-3 small">
              PHI interdit (nom, prénom, IPP, date de naissance). La version est créée <strong>inactive</strong> — activez-la explicitement.
            </div>

            {/* Type de tâche */}
            <div className="mb-3">
              <label className="form-label small fw-semibold">Type de tâche</label>
              <select
                className="form-select form-select-sm"
                value={taskType}
                onChange={e => setTaskType(e.target.value)}
              >
                {TASK_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Texte du prompt */}
            <div className="mb-3">
              <label className="form-label small fw-semibold">
                Texte du prompt
                <span className={`ms-2 small ${overLimit ? 'text-danger fw-bold' : 'text-muted'}`}>
                  {charCount.toLocaleString()} / 16 000
                </span>
              </label>
              <textarea
                className={`form-control font-monospace small ${overLimit ? 'is-invalid' : ''}`}
                rows={10}
                value={promptText}
                onChange={e => setPromptText(e.target.value)}
                placeholder="Décris le rôle et le comportement de l'agent..."
                spellCheck={false}
              />
              {overLimit && (
                <div className="invalid-feedback">Limite de 16 000 caractères dépassée.</div>
              )}
            </div>

            {/* Auteur */}
            <div className="mb-3">
              <label className="form-label small fw-semibold">
                Auteur <span className="text-muted">(optionnel)</span>
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={author}
                onChange={e => setAuthor(e.target.value)}
                placeholder="Votre nom ou identifiant"
                maxLength={100}
              />
            </div>

            {/* Notes */}
            <div className="mb-2">
              <label className="form-label small fw-semibold">
                Note <span className="text-muted">(optionnel — contexte du changement)</span>
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Ex : Amélioration détection doublons, ajout contrainte PHI…"
                maxLength={255}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              Annuler
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSubmit}
              disabled={creating || !promptText.trim() || overLimit}
            >
              {creating
                ? <><span className="spinner-border spinner-border-sm me-1" />Création…</>
                : 'Créer la version'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
